import { Frown, type LucideIcon, Meh, Smile } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { DDS_MOODS, type DDSMood } from '@/types'

interface MoodStyle {
	icon: LucideIcon
	// Classes completas (o Tailwind só gera o que aparece literalmente no código)
	text: string
	idle: string
	selected: string
}

// Verde / amarelo / vermelho
export const DDS_MOOD_STYLES: Record<DDSMood, MoodStyle> = {
	HAPPY: {
		icon: Smile,
		text: 'text-kaefer-grass-green',
		idle: 'border-kaefer-grass-green/40 bg-kaefer-grass-green/5 text-kaefer-grass-green',
		selected:
			'border-kaefer-grass-green bg-kaefer-grass-green text-white ring-4 ring-kaefer-grass-green/25',
	},
	NEUTRAL: {
		icon: Meh,
		text: 'text-kaefer-honey-yellow',
		idle: 'border-kaefer-honey-yellow/50 bg-kaefer-honey-yellow/5 text-kaefer-honey-yellow',
		selected:
			'border-kaefer-honey-yellow bg-kaefer-honey-yellow text-kaefer-dark-grey ring-4 ring-kaefer-honey-yellow/30',
	},
	SAD: {
		icon: Frown,
		text: 'text-kaefer-red',
		idle: 'border-kaefer-red/40 bg-kaefer-red/5 text-kaefer-red',
		selected:
			'border-kaefer-red bg-kaefer-red text-white ring-4 ring-kaefer-red/25',
	},
}

interface DdsMoodIconProps {
	mood: DDSMood
	className?: string
}

export const DdsMoodIcon = ({ mood, className }: DdsMoodIconProps) => {
	const { t } = useTranslation()
	const { icon: Icon, text } = DDS_MOOD_STYLES[mood]

	return (
		<Icon
			aria-label={t(`ddsMood.${mood}`)}
			className={cn('size-4', text, className)}
			role="img"
		/>
	)
}

interface DdsMoodPickerProps {
	value: DDSMood | null
	onChange: (mood: DDSMood) => void
	disabled?: boolean
}

export const DdsMoodPicker = ({
	value,
	onChange,
	disabled,
}: DdsMoodPickerProps) => {
	const { t } = useTranslation()

	return (
		<div
			aria-label={t('ddsCheckIn.mood.question')}
			className="grid grid-cols-3 gap-3"
			role="radiogroup"
		>
			{DDS_MOODS.map((mood) => {
				const { icon: Icon, idle, selected } = DDS_MOOD_STYLES[mood]
				const isSelected = value === mood

				return (
					<button
						aria-checked={isSelected}
						className={cn(
							'flex flex-col items-center gap-2 rounded-xl border-2 px-2 py-5 font-semibold text-sm transition-all active:scale-95 disabled:opacity-50',
							isSelected ? selected : idle,
						)}
						disabled={disabled}
						key={mood}
						onClick={() => onChange(mood)}
						role="radio"
						type="button"
					>
						<Icon className="size-12" strokeWidth={1.75} />
						{t(`ddsMood.${mood}`)}
					</button>
				)
			})}
		</div>
	)
}
